import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Segmented, Table, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { apiFetch, apiFetchJson } from "@/utils/apiFetch";
import { Chip, TIER_COLOR } from "./shared";

// ── API ───────────────────────────────────────────────────────────────────────
// The enrichment backlog: golden records whose identity is settled but whose
// data is thin, ranked by gap score (see views_portfolio_mastery_health.py).
// Deliberately excluded from the needs-review counts — the remediation is
// enrichment, not adjudication. Steward state reuses the queue's PATCH
// endpoint under the GAP-<golden_id> item namespace.

export interface GapRecord {
  golden_id: string;
  item_id: string;
  name: string | null;
  city: string | null;
  country: string | null;
  tier: string | null;
  merge_count: number;
  n_src: number;
  gap_score: number;
  flags: string[];
  status: string;
  priority: string | null;
}

interface GapsPayload {
  results: GapRecord[];
  count: number;
  min_gap: number;
  scope: string;
}

const PAGE_SIZE = 50;

async function fetchGaps(scope: string, offset: number): Promise<GapsPayload> {
  return apiFetchJson<GapsPayload>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-gaps/?scope=${scope}&limit=${PAGE_SIZE}&offset=${offset}`,
  );
}

async function dismissGapItem(itemId: string): Promise<void> {
  await apiFetch(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/${encodeURIComponent(itemId)}/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "dismissed" }),
    },
  );
}

// ── table ─────────────────────────────────────────────────────────────────────

export default function DataMasteryGaps() {
  const [scope, setScope] = useState<string>("open");
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["mastery-gaps", scope, page],
    queryFn: () => fetchGaps(scope, (page - 1) * PAGE_SIZE),
    staleTime: 30_000,
  });

  const dismissMutation = useMutation({
    mutationFn: dismissGapItem,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["mastery-gaps"] }),
  });

  const columns: ColumnsType<GapRecord> = [
    {
      key: "name",
      title: "Golden record",
      dataIndex: "name",
      render: (v: string | null, r) =>
        v || <span className="text-[#9aa9b0]">(nameless) {r.golden_id}</span>,
    },
    { key: "city", title: "City", dataIndex: "city", width: 140 },
    { key: "country", title: "Country", dataIndex: "country", width: 110 },
    {
      key: "tier",
      title: "Tier",
      dataIndex: "tier",
      width: 120,
      render: (v: string | null) =>
        v ? <Tag color={TIER_COLOR[v] ?? "default"}>{v}</Tag> : "—",
    },
    {
      key: "n_src",
      title: "Sources",
      dataIndex: "n_src",
      width: 85,
      align: "right",
    },
    {
      key: "gap_score",
      title: "Gap score",
      dataIndex: "gap_score",
      width: 95,
      align: "right",
      render: (v: number) => (
        <Tag color={v >= 6 ? "red" : v >= 5 ? "orange" : "default"}>{v}</Tag>
      ),
    },
    {
      key: "flags",
      title: "Gaps",
      dataIndex: "flags",
      render: (flags: string[]) => (
        <span className="flex flex-wrap gap-1">
          {(flags || []).map((f) => (
            <Chip key={f}>{f}</Chip>
          ))}
        </span>
      ),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      width: 110,
      render: (v: string) =>
        v === "open" ? null : <Tag color="default">{v}</Tag>,
    },
    {
      key: "actions",
      title: "",
      width: 90,
      render: (_: unknown, r) =>
        r.status === "open" ? (
          <Button
            size="small"
            onClick={() => dismissMutation.mutate(r.item_id)}
            loading={
              dismissMutation.isPending &&
              dismissMutation.variables === r.item_id
            }
          >
            Dismiss
          </Button>
        ) : null,
    },
  ];

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <span className="font-mcqueenGrotesk text-sm font-bold text-[#2B4752]">
          Enrichment backlog{" "}
          <Tooltip
            title={`Golden records missing key attributes (gap score ≥ ${data?.min_gap ?? 4}). Separate from the review counts above — the remediation is enrichment, not adjudication.`}
          >
            <InfoCircleOutlined className="ml-1 text-xs text-[#556C75]" />
          </Tooltip>
        </span>
        <Segmented
          size="small"
          value={scope}
          onChange={(v) => {
            setScope(v as string);
            setPage(1);
          }}
          options={[
            { label: "Open", value: "open" },
            { label: "All", value: "all" },
          ]}
        />
      </div>
      <Table<GapRecord>
        size="small"
        rowKey="item_id"
        loading={isLoading}
        columns={columns}
        dataSource={data?.results ?? []}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.count ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </div>
  );
}
